import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { api, ApiError } from '../../api.js';
import { t } from '../../i18n/index.js';
import { refreshCoins } from '../../coins.js';
import { Coins } from '../../components/CoinIcon.js';
import { CardBack, PlayingCard } from '../vale/ValeGame.js';
import { Dealer, FeltMark } from './Dealer.js';
import { BjAudio } from './audio.js';
import { BJ_CHIPS, BJ_SEATS, type HandResult } from '../../../shared/games/blackjack/rules.js';
import type { BjConfig } from '../../../shared/games/blackjack/rules.js';
import type { BjHandView, BjSeatView, BjTableSummary, BjTableView } from '../../../shared/games/blackjack/view.js';

// Blackjack (user decisions 2026-10-01, blackjack-suunnitelma.md): the lobby (at most 4 tables) and the table –
// green felt, the dealer in Areena's jersey, 5 seats with names, chips 5 · 10 · 25 · 100, commentary with
// captions, chat and spectators. The page asks the server for the table a few times a second.

const errorText = (err: unknown) => t.errors[err instanceof ApiError ? err.code : 'network'];
const go = (hash: string) => {
  location.hash = hash;
};

export function BlackjackView() {
  const [id, setId] = useState(() => tableIdFromHash());
  useEffect(() => {
    const f = () => setId(tableIdFromHash());
    addEventListener('hashchange', f);
    return () => removeEventListener('hashchange', f);
  }, []);
  return id ? <BlackjackTable id={id} key={id} /> : <BlackjackLobby />;
}

function tableIdFromHash(): string | null {
  const m = /^#\/blackjack\/([0-9a-f-]{36})$/.exec(location.hash);
  return m ? m[1]! : null;
}

// --- The lobby ----------------------------------------------------------------------------------------

/** The blackjack tables and settings, kept fresh (every 4 s). */
function useBjTables() {
  const [tables, setTables] = useState<BjTableSummary[] | null>(null);
  const [config, setConfig] = useState<BjConfig | null>(null);
  useEffect(() => {
    const load = () =>
      api<{ tables: BjTableSummary[]; config: BjConfig }>('GET', '/api/blackjack/tables')
        .then((r) => {
          setTables(r.tables);
          setConfig(r.config);
        })
        .catch(() => {});
    void load();
    const timer = setInterval(() => void load(), 4000);
    return () => clearInterval(timer);
  }, []);
  return { tables, config };
}

/**
 * The open blackjack tables (user decision 2026-10-01): every table, also a full one – it can be watched
 * while waiting for a seat.
 */
export function BlackjackTableList() {
  const { tables } = useBjTables();
  if (!tables) return null;
  if (tables.length === 0) return <p class="muted empty">Ei avoimia blackjack-pöytiä – avaa ensimmäinen yläpuolelta!</p>;
  return (
    <div class="bj-lobby-list">
      {tables.map((tb) => (
        <div class="bj-lobby-table" key={tb.id}>
          <b>Pöytä {tb.number}</b>
          <span class="muted">{tb.players.length ? tb.players.join(', ') : 'ei pelaajia'}</span>
          <span class="muted">
            {tb.free > 0 ? `${tb.free} vapaata paikkaa` : 'täynnä'}
            {tb.spectators ? ` · 👁 ${tb.spectators}` : ''}
          </span>
          <a class={`btn btn-small ${tb.free > 0 ? 'btn-primary' : ''}`} href={`#/blackjack/${tb.id}`}>
            {tb.free > 0 ? 'Mene pöytään' : 'Katso ja odota paikkaa'}
          </a>
        </div>
      ))}
    </div>
  );
}

export function BlackjackLobby(props: { compact?: boolean; embedded?: boolean }) {
  const { tables, config } = useBjTables();
  const [error, setError] = useState<string | null>(null);
  const open = async () => {
    try {
      const r = await api<{ id: string }>('POST', '/api/blackjack/tables');
      go(`#/blackjack/${r.id}`);
    } catch (err) {
      setError(errorText(err));
    }
  };
  return (
    <section class={props.embedded ? 'bj-lobby embedded' : 'card bj-lobby'}>
      {!props.embedded && <h2>♠ Blackjack</h2>}
      <p class="muted">
        Pelaa tietokonejakajaa vastaan yksin tai porukalla (enintään 5 pelaajaa pöydässä, 4 pöytää). Panokset AreCoineina
        {config ? ` · panos ${config.minBet}–${config.maxBet}` : ''}. Blackjack maksaa 3 : 2, jakaja jää kaikkiin 17:ään.
      </p>
      {error && <p class="error">{error}</p>}
      {/* Embedded in the game grid the tables are listed under "Avoimet pöydät". */}
      {!props.embedded && <BlackjackTableList />}
      {tables && tables.length >= 4 && <p class="muted">Kaikki 4 blackjack-pöytää ovat käytössä – liity johonkin niistä alta.</p>}
      {tables && tables.length < 4 && (
        <button type="button" class="btn btn-primary" onClick={() => void open()}>
          ＋ Avaa uusi pöytä
        </button>
      )}
      {!props.compact && <p class="hint">AreCoin on Areenan leikkivaluutta – sillä ei ole rahallista arvoa.</p>}
    </section>
  );
}

// --- The table ----------------------------------------------------------------------------------------

const RESULT: Record<HandResult, string> = { blackjack: 'BLACKJACK!', win: 'VOITTO', push: 'TASAPELI', lose: 'HÄVIÖ', bust: 'YLI' };

function BlackjackTable(props: { id: string }) {
  const [v, setV] = useState<BjTableView | null>(null);
  const [gone, setGone] = useState<'closed' | 'guest' | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [stake, setStakeState] = useState(0);
  const stakeNow = useRef(0);
  const setStake = (n: number) => {
    stakeNow.current = n;
    setStakeState(n);
  };
  const [chatText, setChatText] = useState('');
  const [, setTick] = useState(0);
  const audio = useMemo(() => new BjAudio(), []);
  const lastVersion = useRef(0);

  const accept = (next: BjTableView) => {
    setV(next);
    audio.update(next.now, next.cues);
    if (next.version !== lastVersion.current && next.phase === 'settle') void refreshCoins();
    lastVersion.current = next.version;
  };

  useEffect(() => {
    audio.start();
    let stopped = false;
    const poll = async () => {
      try {
        const next = await api<BjTableView>('GET', `/api/blackjack/tables/${props.id}`);
        if (!stopped) accept(next);
      } catch (err) {
        if (err instanceof ApiError && (err.code === 'table_not_found' || err.code === 'forbidden')) setGone(err.code === 'forbidden' ? 'guest' : 'closed');
      }
    };
    void poll();
    const timer = setInterval(() => void poll(), 600);
    const clock = setInterval(() => setTick((n) => n + 1), 250);
    return () => {
      stopped = true;
      clearInterval(timer);
      clearInterval(clock);
      audio.stop();
    };
  }, [props.id]);

  // Opening a table: watch first, the player picks a free seat (user request 2026-10-01).
  const joined = useRef(false);
  useEffect(() => {
    if (!v || joined.current) return;
    joined.current = true;
    if (v.me.seat === null && !v.me.spectator) void post('watch');
  }, [v]);

  async function post(path: string, body?: unknown) {
    try {
      accept(await api<BjTableView>('POST', `/api/blackjack/tables/${props.id}/${path}`, body));
      setFlash(null);
      return true;
    } catch (err) {
      setFlash(errorText(err));
      return false;
    }
  }

  if (gone) {
    return (
      <section class="card">
        <h2>{gone === 'guest' ? 'Blackjack ei ole vieraille' : 'Pöytä on suljettu'}</h2>
        <p class="muted">{gone === 'guest' ? 'Blackjackia pelataan AreCoineilla, jotka ovat vain rekisteröityneillä pelaajilla.' : 'Pöydässä ei ole enää pelaajia.'}</p>
        <a class="btn btn-primary" href="#/tables">
          Pelipöytiin
        </a>
      </section>
    );
  }
  if (!v) return <section class="card muted">{t.status.connecting}</section>;

  const now = audio.serverNow();
  const me = v.me.seat !== null ? v.seats[v.me.seat]! : null;
  const left = v.deadline ? Math.max(0, Math.ceil((v.deadline - now) / 1000)) : null;
  const captions = v.cues.filter((c) => c.kind === 'voice' && c.caption && c.at <= now + 200).slice(-4);
  const leave = async () => {
    await post('leave');
    void refreshCoins();
    go('#/tables');
  };
  const bet = async (amount = stake) => {
    if (amount <= 0) return setFlash('Valitse panos pelimerkeillä.');
    if (await post('bet', { amount })) {
      setStake(0);
      void refreshCoins();
    }
  };
  const addChip = (c: number) => {
    stakeNow.current = Math.min(v.config.maxBet, stakeNow.current + c);
    setStake(stakeNow.current);
  };
  // A double click on a chip places the bet at once (user request 2026-10-01): its two clicks count as one chip.
  const quickBet = (c: number) => {
    const amount = Math.min(v.config.maxBet, Math.max(c, stakeNow.current - c));
    stakeNow.current = 0;
    setStake(amount);
    void bet(amount);
  };
  const myTurn = v.phase === 'playing' && v.turn?.seat === v.me.seat;

  return (
    <div class="bj-page">
      <div class="bj-top">
        <span class="pill">
          ♠ Blackjack · Pöytä {v.number} · panos {v.config.minBet}–{v.config.maxBet}
        </span>
        {v.spectators.length > 0 && <span class="pill" title={v.spectators.join(', ')}>👁 {v.spectators.length}</span>}
        <button class={`pill ${audio.prefs.sound ? 'on' : ''}`} onClick={() => (audio.set({ sound: !audio.prefs.sound }), setTick((n) => n + 1))}>
          {audio.prefs.sound ? '🔊 Selostus' : '🔇 Selostus'}
        </button>
        <button class={`pill ${audio.prefs.music ? 'on' : ''}`} onClick={() => (audio.set({ music: !audio.prefs.music }), setTick((n) => n + 1))}>
          🎵 Musiikki {audio.prefs.music ? 'päällä' : 'pois'}
        </button>
        {audio.blocked && audio.prefs.sound && <span class="pill warn">Napauta sivua – äänet alkavat</span>}
        <span class="bj-spacer" />
        {v.me.balance !== null && <Coins amount={v.me.balance} class="bj-balance" />}
        <button class="btn btn-small" onClick={() => void leave()}>
          Poistu pöydästä
        </button>
      </div>

      <div class="bj-main">
        <div class="bj-stage">
          <div class="bj-felt">
            <FeltMark />
            <div class="bj-shoe" title={`Kengässä jäljellä ${Math.round(v.shoeLeft * 100)} %`} />
            <Dealer />
            <div class="bj-dealer-hand">
              {v.dealer.total !== null && <span class="bj-badge">Jakaja {v.dealer.total}</span>}
              <div class="bj-cards">{v.dealer.cards.map((c, i) => (c === null ? <CardBack key={`b${i}`} /> : <PlayingCard key={i} card={c} />))}</div>
            </div>
            <div class="bj-arc">
              BLACKJACK MAKSAA 3 : 2<small>JAKAJA JÄÄ KAIKKIIN 17:ÄÄN · VAKUUTUS MAKSAA 2 : 1</small>
            </div>
            <div class="bj-seats">
              {Array.from({ length: BJ_SEATS }, (_, i) => (
                <SeatBox key={i} index={i} seat={v.seats[i] ?? null} view={v} onSit={() => void post('sit', { seat: i })} />
              ))}
            </div>
          </div>

          <div class="bj-controls">
            {left !== null && (
              <div class="bj-timer">
                ⏱ {v.phase === 'betting' ? 'Panosaika' : v.phase === 'insurance' ? 'Vakuutus' : myTurn ? 'Vuorosi' : 'Vuoro'}: {left} s
              </div>
            )}
            {flash && <p class="error">{flash}</p>}
            {me && v.me.can.bet && (
              <div class="bj-bet">
                {BJ_CHIPS.map((c) => (
                  <button key={c} class={`bj-chip c${c}`} onClick={() => addChip(c)} onDblClick={() => quickBet(c)} aria-label={`Lisää ${c}`} title="Napauta: lisää · tuplaklikkaa: aseta panos heti">
                    {c}
                  </button>
                ))}
                <span class="bj-hint">tuplaklikkaus = aseta heti</span>
                <span class="bj-stake">
                  Panos <b>{stake}</b>
                </span>
                <button class="btn btn-primary" disabled={stake < v.config.minBet} onClick={() => void bet()}>
                  Aseta panos
                </button>
                {v.me.lastBet && (
                  <button class="btn btn-small" onClick={() => setStake(v.me.lastBet!)}>
                    Sama kuin viimeksi ({v.me.lastBet})
                  </button>
                )}
                <button class="btn btn-small" disabled={stake === 0} onClick={() => setStake(0)}>
                  Tyhjennä
                </button>
              </div>
            )}
            {me && v.phase === 'betting' && me.bet !== null && <p class="muted">Panos {me.bet} asetettu – odotetaan muita…</p>}
            {me && v.phase === 'betting' && v.me.balance !== null && v.me.balance < v.config.minBet && (
              <p class="error">
                Saldo ei riitä. {v.me.canRefill ? <a href="#/account">Hae päivän täydennys Oma tili -sivulta.</a> : 'Täydennys onnistuu taas huomenna.'}
              </p>
            )}
            {v.me.can.insurance && me && (
              <div class="bj-actions">
                <span>Jakajalla ässä – vakuutus {Math.floor((me.bet ?? 0) / 2)}?</span>
                <button class="btn btn-primary" onClick={() => void post('insurance', { take: true })}>
                  Vakuuta
                </button>
                <button class="btn" onClick={() => void post('insurance', { take: false })}>
                  Ei vakuutusta
                </button>
              </div>
            )}
            {myTurn && (
              <div class="bj-actions">
                <button class="btn btn-primary" disabled={!v.me.can.hit} onClick={() => void post('action', { action: 'hit' })}>
                  Kortti
                </button>
                <button class="btn" disabled={!v.me.can.stand} onClick={() => void post('action', { action: 'stand' })}>
                  Jää
                </button>
                <button class="btn" disabled={!v.me.can.double} onClick={() => void post('action', { action: 'double' }).then(() => refreshCoins())}>
                  Tuplaa
                </button>
                <button class="btn" disabled={!v.me.can.split} onClick={() => void post('action', { action: 'split' }).then(() => refreshCoins())}>
                  Jaa
                </button>
              </div>
            )}
            {!me && <p class="bj-pick">{v.seats.some((s) => s === null) ? '👆 Valitse pöydästä vapaa paikka (Istu tähän).' : 'Pöytä on täynnä – katsot peliä.'}</p>}
          </div>
        </div>

        <aside class="bj-side">
          <div class="bj-box bj-captions" aria-live="polite">
            <h3>🔊 Selostus</h3>
            {captions.length === 0 ? <p class="muted">–</p> : captions.map((c, i) => <p key={c.id} class={i === captions.length - 1 ? 'new' : ''}>{c.caption}</p>)}
          </div>
          <div class="bj-box bj-chat">
            <h3>💬 Pöydän chat</h3>
            <div class="bj-chat-lines">
              {v.chat.map((m) => (
                <p key={m.id}>
                  <b>{m.name}:</b> {m.text}
                </p>
              ))}
            </div>
            <form
              class="bj-chat-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (chatText.trim()) void post('chat', { text: chatText.trim() }).then((ok) => ok && setChatText(''));
              }}
            >
              <input value={chatText} maxLength={200} onInput={(e) => setChatText(e.currentTarget.value)} placeholder="Kirjoita viesti…" />
            </form>
          </div>
          <p class="hint">Leikkivaluutta AreCoin – ei rahallista arvoa.</p>
        </aside>
      </div>
    </div>
  );
}

function SeatBox(props: { index: number; seat: BjSeatView | null; view: BjTableView; onSit: () => void }) {
  const { seat, view, index } = props;
  const turn = view.turn?.seat === index;
  if (!seat) {
    return (
      <div class={`bj-seat empty s${index}`}>
        <div class="bj-spot">vapaa</div>
        {view.me.seat === null ? (
          <button class="btn btn-small btn-primary" onClick={props.onSit}>
            Istu tähän
          </button>
        ) : view.phase === 'betting' && view.seats[view.me.seat]?.bet === null ? (
          <button class="btn btn-small" onClick={props.onSit} title="Vaihda paikkaa">
            Siirry tähän
          </button>
        ) : (
          <span class="bj-name muted">Paikka {index + 1}</span>
        )}
      </div>
    );
  }
  return (
    <div class={`bj-seat s${index} ${seat.hands.length === 0 ? 'idle' : ''} ${turn ? 'active' : ''} ${seat.userId === view.seats[view.me.seat ?? -1]?.userId ? 'mine' : ''}`}>
      {seat.hands.map((h, j) => (
        <HandBox key={j} hand={h} active={turn && view.turn?.hand === j} />
      ))}
      {seat.bet === null && view.phase !== 'betting' && <div class="bj-spot small">ei mukana</div>}
      {seat.insurance !== null && <span class="bj-badge ins">vakuutus {seat.insurance}</span>}
      <div class="bj-name">
        {seat.name}
        {seat.leaving ? ' (lähtee)' : ''}
      </div>
    </div>
  );
}

function HandBox(props: { hand: BjHandView; active: boolean }) {
  const h = props.hand;
  const badge = h.result ? RESULT[h.result] : h.total > 21 ? `YLI ${h.total}` : h.cards.length ? `${h.soft && h.total < 21 ? 'pehmeä ' : ''}${h.total}` : null;
  const net = h.payout !== null ? h.payout - h.bet : null;
  return (
    <div class={`bj-hand ${props.active ? 'active' : ''}`}>
      <div class="bj-cards small">
        {h.cards.map((c, i) => (
          <PlayingCard key={i} card={c} small />
        ))}
      </div>
      {/* The badge's place is kept before the cards come, so the chips stay put (user request 2026-10-02). */}
      {badge ? <span class={`bj-badge r-${h.result ?? (h.total > 21 ? 'bust' : 'open')}`}>{badge}</span> : <span class="bj-badge placeholder" aria-hidden="true">&nbsp;</span>}
      <div class="bj-chips">
        <span class="bj-chip mini">{h.bet}</span>
        {h.doubled && <span class="bj-tag">×2</span>}
        {net !== null && <span class={`bj-net ${net > 0 ? 'plus' : net < 0 ? 'minus' : ''}`}>{net > 0 ? `+${net}` : net}</span>}
      </div>
    </div>
  );
}
